import { For, Show, createSignal } from "solid-js";

import { useAccount, useAccounts } from "~/lib/hooks";

type Props = {
  wsKey: () => string;
  // Selected account ID. Pass null/undefined for "no selection".
  value: () => string | null;
  // Called with the new account ID (or null when the user clears).
  onChange: (accountId: string | null, name: string | null) => void;
  // Optional pre-fetched display name for the current value, so the chip
  // shows the right label without an extra GET.
  selectedName?: () => string | null;
  // Optional ARIA label override for the search input.
  label?: string;
  // Optional placeholder.
  placeholder?: string;
  // Render compact (smaller chip + input).
  size?: "sm" | "md";
  // Disabled state.
  disabled?: boolean;
};

export function AccountPicker(props: Props) {
  const [query, setQuery] = createSignal("");
  const [open, setOpen] = createSignal(false);

  // Fetch matches whenever the picker is open and the user has typed.
  const accounts = useAccounts(props.wsKey, () => ({
    q: query() || undefined,
    limit: 12,
  }));

  // If the parent didn't pre-supply a name (e.g. the form just loaded with a
  // persisted lookup-field ULID), auto-resolve so the chip shows the name.
  const resolved = useAccount(props.wsKey, () => props.value() ?? "");

  const chipLabel = () => {
    const supplied = props.selectedName?.();
    if (supplied) return supplied;
    if (resolved.data) return resolved.data.name;
    return props.value()?.slice(0, 14) ?? "Account";
  };

  const size = () => props.size ?? "md";
  const inputCls = () =>
    size() === "sm" ? "input input-bordered input-sm w-full" : "input input-bordered w-full";

  const clear = () => {
    props.onChange(null, null);
    setQuery("");
  };

  const pick = (id: string, name: string) => {
    props.onChange(id, name);
    setQuery("");
    setOpen(false);
  };

  return (
    <div class="relative">
      <Show
        when={props.value()}
        fallback={
          <div>
            <input
              type="text"
              class={inputCls()}
              placeholder={props.placeholder ?? "Search accounts…"}
              aria-label={props.label ?? "Search accounts"}
              value={query()}
              disabled={props.disabled}
              onInput={(e) => {
                setQuery(e.currentTarget.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                // Close after the click-to-pick has a chance to register.
                window.setTimeout(() => setOpen(false), 150);
              }}
              autocomplete="off"
            />
            <Show when={open() && query().length > 0}>
              <ul
                role="listbox"
                aria-label="Account matches"
                class="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg bg-base-100 shadow-lg border border-base-300"
              >
                <For each={accounts.data?.items ?? []}>
                  {(a) => (
                    <li>
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        class="w-full text-left px-3 py-2 hover:bg-base-200"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pick(a.id, a.name)}
                      >
                        <div class="font-medium">{a.name}</div>
                        <Show when={a.primary_domain}>
                          <div class="text-xs opacity-60">{a.primary_domain}</div>
                        </Show>
                      </button>
                    </li>
                  )}
                </For>
                <Show when={(accounts.data?.items.length ?? 0) === 0 && !accounts.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">No accounts match.</li>
                </Show>
                <Show when={accounts.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">Searching…</li>
                </Show>
              </ul>
            </Show>
          </div>
        }
      >
        <div
          class={`badge ${
            size() === "sm" ? "badge-md" : "badge-lg"
          } badge-primary badge-outline gap-2`}
        >
          <span class="truncate max-w-xs">{chipLabel()}</span>
          <button
            type="button"
            class="btn btn-ghost btn-xs btn-circle"
            aria-label="Clear account"
            disabled={props.disabled}
            onClick={clear}
          >
            ✕
          </button>
        </div>
      </Show>
    </div>
  );
}
